import { Injectable } from '@angular/core';
import { TableColumnService } from '../services/table-column.service';
import { TableColumnProvider } from '../../../interfaces/table-column-provider';
import { TableColumn } from '../../../models/table-column';
import { ShipmentHeader } from '../../../models/shipment-header';
import { CountriesService } from '../services/countries.service';
import { EntityEnum } from '../../../enum/entity.enum';

@Injectable({
    providedIn: 'root'
})
export class ShipmentHeaderCardsService implements TableColumnProvider {

    public readonly SHIPPER_ATTN = 'SHIPPER_ATTN';
    public readonly SHIPPER_ORGANIZATION = 'SHIPPER_ORGANIZATION';
    public readonly SHIPPER_STREET1 = 'SHIPPER_STREET1';
    public readonly SHIPPER_STREET2 = 'SHIPPER_STREET2';
    public readonly SHIPPER_CITY = 'SHIPPER_CITY';
    public readonly SHIPPER_PROVINCE = 'SHIPPER_PROVINCE';
    public readonly SHIPPER_COUNTRY = 'SHIPPER_COUNTRY';
    public readonly SHIPPER_POSTALCODE = 'SHIPPER_POSTALCODE';
    public readonly SHIPPER_TEL = 'SHIPPER_TEL';
    public readonly SHIPPER_EMAIL = 'SHIPPER_EMAIL';

    public readonly DESTINATION_ATTN = 'DESTINATION_ATTN';
    public readonly DESTINATION_ORGANIZATION = 'DESTINATION_ORGANIZATION';
    public readonly DESTINATION_STREET1 = 'DESTINATION_STREET1';
    public readonly DESTINATION_STREET2 = 'DESTINATION_STREET2';
    public readonly DESTINATION_CITY = 'DESTINATION_CITY';
    public readonly DESTINATION_PROVINCE = 'DESTINATION_PROVINCE';
    public readonly DESTINATION_COUNTRY = 'DESTINATION_COUNTRY';
    public readonly DESTINATION_POSTALCODE = 'DESTINATION_POSTALCODE';
    public readonly DESTINATION_TEL = 'DESTINATION_TEL';
    public readonly DESTINATION_EMAIL = 'DESTINATION_EMAIL';

    constructor(private countriesService: CountriesService, private tableColumnService: TableColumnService) {
        this.tableColumnService.registerProvider(this);
    }

    supports(entityClass: string): boolean {
        return entityClass === EntityEnum.SHIPMENT_HEADER;
    }

    get(id: string): TableColumn {
        switch (id) {
            case this.SHIPPER_ATTN:
                return {
                    id: this.SHIPPER_ATTN,
                    width: 250,
                    caption: $localize`Shipper Attn`,
                    sort: 'shipper.attn',
                    render: (entity: ShipmentHeader) => entity.shipper.attn ?? ''
                };
            case this.SHIPPER_ORGANIZATION:
                return {
                    id: this.SHIPPER_ORGANIZATION,
                    width: 250,
                    caption: $localize`Shipper Company`,
                    sort: 'shipper.orgname',
                    render: (entity: ShipmentHeader) => entity.shipper.organization ?? ''
                };
            case this.SHIPPER_STREET1:
                return {
                    id: this.SHIPPER_STREET1,
                    width: 200,
                    caption: $localize`Shipper Street #1`,
                    sort: 'shipper.street1',
                    render: (entity: ShipmentHeader) => entity.shipper.street1 ?? ''
                };
            case this.SHIPPER_STREET2:
                return {
                    id: this.SHIPPER_STREET2,
                    width: 200,
                    caption: $localize`Shipper Street #2`,
                    sort: 'shipper.street2',
                    render: (entity: ShipmentHeader) => entity.shipper.street2 ?? ''
                };
            case this.SHIPPER_CITY:
                return {
                    id: this.SHIPPER_CITY,
                    width: 150,
                    caption: $localize`Shipper City`,
                    sort: 'shipper.city',
                    render: (entity: ShipmentHeader) => entity.shipper.city ?? ''
                };
            case this.SHIPPER_PROVINCE:
                return {
                    id: this.SHIPPER_PROVINCE,
                    width: 120,
                    caption: $localize`Shipper Province/State`,
                    render: (entity: ShipmentHeader) => this.countriesService.provinceName(entity.shipper.country, entity.shipper.province)
                };
            case this.SHIPPER_POSTALCODE:
                return {
                    id: this.SHIPPER_POSTALCODE,
                    width: 100,
                    caption: $localize`Shipper Postal Code/ZIP`,
                    sort: 'shipper.postalcode',
                    render: (entity: ShipmentHeader) => entity.shipper.postalCode ?? ''
                };
            case this.SHIPPER_COUNTRY:
                return {
                    id: this.SHIPPER_COUNTRY,
                    width: 100,
                    caption: $localize`Shipper Country`,
                    sort: 'shipper.country',
                    render: (entity: ShipmentHeader) => this.countriesService.countryName(entity.shipper.country)
                };
            case this.SHIPPER_TEL:
                return {
                    id: this.SHIPPER_TEL,
                    width: 100,
                    caption: $localize`Shipper Telephone`,
                    sort: 'shipper.telephone',
                    render: (entity: ShipmentHeader) => entity.shipper.telephone ?? ''
                };
            case this.SHIPPER_EMAIL:
                return {
                    id: this.SHIPPER_EMAIL,
                    width: 120,
                    caption: $localize`Shipper Email`,
                    sort: 'shipper.email',
                    render: (entity: ShipmentHeader) => entity.shipper.email ?? ''
                };
            case this.DESTINATION_ATTN:
                return {
                    id: this.DESTINATION_ATTN,
                    width: 250,
                    caption: $localize`Destination Attn`,
                    sort: 'destination.attn',
                    render: (entity: ShipmentHeader) => entity.destination.attn ?? ''
                };
            case this.DESTINATION_ORGANIZATION:
                return {
                    id: this.DESTINATION_ORGANIZATION,
                    width: 250,
                    caption: $localize`Destination Company`,
                    sort: 'destination.orgname',
                    render: (entity: ShipmentHeader) => entity.destination.organization ?? ''
                };
            case this.DESTINATION_STREET1:
                return {
                    id: this.DESTINATION_STREET1,
                    width: 200,
                    caption: $localize`Destination Street #1`,
                    sort: 'destination.street1',
                    render: (entity: ShipmentHeader) => entity.destination.street1 ?? ''
                };
            case this.DESTINATION_STREET2:
                return {
                    id: this.DESTINATION_STREET2,
                    width: 200,
                    caption: $localize`Destination Street #2`,
                    sort: 'destination.street2',
                    render: (entity: ShipmentHeader) => entity.destination.street2 ?? ''
                };
            case this.DESTINATION_CITY:
                return {
                    id: this.DESTINATION_CITY,
                    width: 150,
                    caption: $localize`Destination City`,
                    sort: 'destination.city',
                    render: (entity: ShipmentHeader) => entity.destination.city ?? ''
                };
            case this.DESTINATION_PROVINCE:
                return {
                    id: this.DESTINATION_PROVINCE,
                    width: 100,
                    caption: $localize`Destination Province/State`,
                    sort: 'destination.province',
                    render: (entity: ShipmentHeader) =>
                        this.countriesService.provinceName(entity.destination.country, entity.destination.province)
                };
            case this.DESTINATION_POSTALCODE:
                return {
                    id: this.DESTINATION_POSTALCODE,
                    width: 100,
                    caption: $localize`Destination Postal Code/ZIP`,
                    sort: 'destination.postalcode',
                    render: (entity: ShipmentHeader) => entity.destination.postalCode ?? ''
                };
            case this.DESTINATION_COUNTRY:
                return {
                    id: this.DESTINATION_COUNTRY,
                    width: 200,
                    caption: $localize`Destination Country`,
                    sort: 'destination.country',
                    render: (entity: ShipmentHeader) => this.countriesService.countryName(entity.destination.country)
                };
            case this.DESTINATION_TEL:
                return {
                    id: this.DESTINATION_TEL,
                    width: 200,
                    caption: $localize`Destination Telephone`,
                    sort: 'destination.telephone',
                    render: (entity: ShipmentHeader) => entity.destination.telephone ?? ''
                };
            case this.DESTINATION_EMAIL:
                return {
                    id: this.DESTINATION_EMAIL,
                    width: 200,
                    caption: $localize`Destination Email`,
                    sort: 'destination.email',
                    render: (entity: ShipmentHeader) => entity.destination.email ?? ''
                };
            default:
                throw new Error(`Cannot find column ${id}`);
        }
    }

    ids(): string[] {
        return [
            this.SHIPPER_ATTN,
            this.SHIPPER_ORGANIZATION,
            this.SHIPPER_STREET1,
            this.SHIPPER_STREET2,
            this.SHIPPER_CITY,
            this.SHIPPER_PROVINCE,
            this.SHIPPER_COUNTRY,
            this.SHIPPER_POSTALCODE,
            this.SHIPPER_TEL,
            this.SHIPPER_EMAIL,

            this.DESTINATION_ATTN,
            this.DESTINATION_ORGANIZATION,
            this.DESTINATION_STREET1,
            this.DESTINATION_STREET2,
            this.DESTINATION_CITY,
            this.DESTINATION_PROVINCE,
            this.DESTINATION_COUNTRY,
            this.DESTINATION_POSTALCODE,
            this.DESTINATION_TEL,
            this.DESTINATION_EMAIL,
        ];
    }
}
